/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { Calendar, ExternalLink, MapPin, MessageCircle, Pencil, Share2, UserCheck, Users } from "lucide-react";

import AdventureHero from "@/components/adventures/AdventureHero";
import AddToCalendarButton from "@/components/AddToCalendarButton";
import FullScreenImage from "@/components/FullScreenImage";
import KindredShareSheet from "@/components/KindredShareSheet";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import {
  buildKindredExternalShareText,
  buildKindredWebUrl,
} from "@/lib/kindredShare";
import { dispatchNotification } from "@/lib/pushNotifications";
import {
  formatAgeRange,
  formatDateRange,
  getTypeConfig,
  stripAdventureMeta,
} from "@/lib/adventure-helpers";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import {
  buildAdventureMembershipCounts,
  canPostAdventureMemory,
  getAdventureStatusConfirmation,
  getAdventureMemoryGuidance,
  isAdventureFull,
  isAdventureComplete,
} from "../../shared/activities";
import { normalizeExternalUrl, normalizeWhatsAppChatUrl } from "../../shared/community";
import {
  buildAdventureStatusNotificationBody,
  getAdventureStatusNotificationType,
} from "../../shared/system";
import {
  buildGoogleMapsDirectionsUrl,
  buildGoogleMapsSearchUrl,
  hasMapTarget,
} from "../../shared/location";
import {
  canManageAdventureChecklist,
  canTickAdventureChecklist,
  getAdventureChecklistGuidance,
  getAdventureChecklistSummary,
} from "../../shared/activities";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCheckbox,
  AppEmptyState,
  AppErrorState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";

type AdventureTask = Tables<"adventure_tasks">;
type AdventureMemory = Tables<"adventure_memories"> & {
  profiles?: { display_name: string | null; avatar_url: string | null } | null;
};

type MetaRowProps = {
  icon: LucideIcon;
  children: ReactNode;
};

function DetailMetaRow({ icon: Icon, children }: MetaRowProps) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Icon size={16} className="shrink-0 text-primary" />
      <span>{children}</span>
    </div>
  );
}

const AdventureDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, role } = useAuth();

  const [adventure, setAdventure] = useState<any>(null);
  const [organiser, setOrganiser] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [tasks, setTasks] = useState<AdventureTask[]>([]);
  const [memories, setMemories] = useState<AdventureMemory[]>([]);
  const [myStatus, setMyStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [taskDraft, setTaskDraft] = useState("");
  const [memoryCaption, setMemoryCaption] = useState("");
  const [memoryPhotoUrl, setMemoryPhotoUrl] = useState<string | null>(null);
  const [addingTask, setAddingTask] = useState(false);
  const [postingMemory, setPostingMemory] = useState(false);
  const [togglingTaskId, setTogglingTaskId] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [resolvedMemoryImageUrls, setResolvedMemoryImageUrls] = useState<Map<string, string>>(new Map());
  const chatRequested = searchParams.get("chat") === "1";

  const fetchData = useCallback(async () => {
    if (!id) return;

    const [
      { data: adv },
      { data: mems },
      { data: taskRows, error: taskError },
      { data: memoryRows, error: memoriesError },
    ] = await Promise.all([
      supabase.from("adventures").select("*").eq("id", id).eq("status", "active").maybeSingle(),
      supabase.from("adventure_members").select("*, profiles(display_name)").eq("adventure_id", id),
      supabase
        .from("adventure_tasks")
        .select("id, adventure_id, created_by, title, completed, completed_by, created_at")
        .eq("adventure_id", id)
        .order("created_at", { ascending: true }),
      supabase
        .from("adventure_memories")
        .select("id, adventure_id, user_id, caption, image_url, created_at, profiles(display_name, avatar_url)")
        .eq("adventure_id", id)
        .order("created_at", { ascending: false }),
    ]);

    if (taskError) {
      console.error("Could not load adventure checklist", taskError);
    }

    if (memoriesError) {
      console.error("Could not load adventure memories", memoriesError);
    }

    if (adv) {
      setAdventure(adv);
      const { data: org } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url, created_at")
        .eq("id", adv.user_id)
        .single();
      setOrganiser(org);
    } else {
      setAdventure(null);
      setOrganiser(null);
    }

    if (mems) {
      setMembers(mems);
      const mine = mems.find((member: any) => member.user_id === user?.id);
      setMyStatus(mine?.status ?? null);
    } else {
      setMembers([]);
      setMyStatus(null);
    }

    setTasks((taskRows ?? []) as AdventureTask[]);
    setMemories((memoryRows ?? []) as AdventureMemory[]);
    setLoading(false);
  }, [id, user?.id]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    let active = true;
    const imageValues = memories.map((memory) => memory.image_url).filter(Boolean);

    void resolveSignedStorageUrls("adventure-photos", imageValues).then((nextMap) => {
      if (active) {
        setResolvedMemoryImageUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [memories]);

  useEffect(() => {
    if (!chatRequested || !id) {
      return;
    }
    navigate(`/adventures/${id}/chat`, { replace: true });
  }, [chatRequested, id, navigate]);

  const join = async (status: "interested" | "joined" | "waitlist") => {
    if (!user || !id || acting) return;

    setActing(true);
    const response = myStatus
      ? await supabase.from("adventure_members").update({ status }).eq("adventure_id", id).eq("user_id", user.id)
      : await supabase.from("adventure_members").insert({ adventure_id: id, user_id: user.id, status });

    if (response.error) {
      toast({ title: "Could not update activity", description: response.error.message, variant: "destructive" });
      setActing(false);
      return;
    }

    const notificationStatus = status === "joined" || status === "interested" ? status : null;
    const notificationType = getAdventureStatusNotificationType(notificationStatus);
    if (notificationStatus && notificationType && adventure?.user_id && adventure.user_id !== user.id && myStatus !== status) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("id", user.id)
        .maybeSingle();

      await dispatchNotification({
        userId: adventure.user_id,
        type: notificationType,
        body: buildAdventureStatusNotificationBody({
          actorName: profile?.display_name,
          adventureTitle: adventure.title,
          status: notificationStatus,
        }),
        url: `/adventures/${id}`,
        entityType: "adventure",
        entityId: id,
      });
    }

    toast({ title: getAdventureStatusConfirmation(status) });
    await fetchData();
    setActing(false);
  };

  const leave = async () => {
    if (!user || !id || acting) return;

    setActing(true);
    const { error } = await supabase.from("adventure_members").delete().eq("adventure_id", id).eq("user_id", user.id);

    if (error) {
      toast({ title: "Could not update activity", description: error.message, variant: "destructive" });
      setActing(false);
      return;
    }

    toast({ title: "You've left this adventure" });
    await fetchData();
    setActing(false);
  };

  const addTask = async () => {
    if (!user || !id || !taskDraft.trim() || addingTask) return;

    setAddingTask(true);
    const { error } = await supabase.from("adventure_tasks").insert({
      adventure_id: id,
      created_by: user.id,
      title: taskDraft.trim(),
    });
    setAddingTask(false);

    if (error) {
      toast({ title: "Could not add task", description: error.message, variant: "destructive" });
      return;
    }

    setTaskDraft("");
    await fetchData();
    toast({ title: "Checklist updated" });
  };

  const toggleTask = async (task: AdventureTask) => {
    if (!user || togglingTaskId) return;

    setTogglingTaskId(task.id);
    const nextCompleted = !task.completed;
    const { error } = await supabase
      .from("adventure_tasks")
      .update({
        completed: nextCompleted,
        completed_by: nextCompleted ? user.id : null,
      })
      .eq("id", task.id);
    setTogglingTaskId(null);

    if (error) {
      toast({ title: "Could not update task", description: error.message, variant: "destructive" });
      return;
    }

    await fetchData();
  };

  const addMemory = async () => {
    if (!user || !id || !memoryCaption.trim() || postingMemory || !adventure) return;

    setPostingMemory(true);
    const { error } = await supabase.from("adventure_memories").insert({
      adventure_id: id,
      user_id: user.id,
      caption: memoryCaption.trim(),
      image_url: memoryPhotoUrl ?? adventure.image_url ?? null,
    });
    setPostingMemory(false);

    if (error) {
      toast({ title: "Could not save memory", description: error.message, variant: "destructive" });
      return;
    }

    setMemoryCaption("");
    setMemoryPhotoUrl(null);
    await fetchData();
    toast({ title: "Memory saved" });
  };

  if (loading) {
    return (
      <MemberPageScaffold title="Activity" subtitle="Loading the activity details." fallbackPath="/activities">
        <AppLoadingState title="Loading activity" body="Pulling together the organiser, checklist, and chat." />
      </MemberPageScaffold>
    );
  }

  if (!adventure) {
    return (
      <MemberPageScaffold title="Activity" subtitle="This activity is no longer available." fallbackPath="/activities">
        <AppErrorState
          title="Activity not found"
          body="It may have been removed or the link may be out of date."
          action={
            <AppButton type="button" tone="outline" onPress={() => navigate("/activities")}>
              Back to Activities
            </AppButton>
          }
        />
      </MemberPageScaffold>
    );
  }

  const typeConfig = getTypeConfig(adventure.type);
  const counts = buildAdventureMembershipCounts(members);
  const interestedCount = counts.interested;
  const goingCount = counts.going;
  const waitlistCount = counts.waitlist;
  const goingMembers = members.filter((member) => member.status === "joined");
  const adventureIsFull = isAdventureFull(adventure.max_group_size, counts);
  const completedTaskCount = tasks.filter((task) => task.completed).length;
  const cleanedDescription = stripAdventureMeta(adventure.description);
  const isOwner = canManageAdventureChecklist(user?.id, adventure.user_id);
  const canEditAdventure = isOwner || role === "admin";
  const canAccessChat = isOwner || myStatus === "joined";
  const canToggleTasks = canTickAdventureChecklist(user?.id, adventure.user_id, myStatus);
  const checklistSummary = getAdventureChecklistSummary(tasks.length, completedTaskCount);
  const checklistGuidance = getAdventureChecklistGuidance(isOwner, canToggleTasks, tasks.length > 0);
  const adventureComplete = isAdventureComplete(adventure.date_start, adventure.date_end);
  const canPostMemories = canPostAdventureMemory(user?.id, adventure.user_id, myStatus, adventureComplete);
  const memoryGuidance = getAdventureMemoryGuidance(
    adventureComplete,
    canPostMemories,
    memories.length > 0,
    Boolean(adventure.image_url),
  );
  const sharePayload = {
    version: 1 as const,
    type: "activity" as const,
    targetId: adventure.id,
    title: adventure.title,
    subtitle: `${formatDateRange(adventure.date_start, adventure.date_end)} · ${adventure.location}`,
    imageUrl: adventure.image_url ?? null,
    route: `/adventures/${adventure.id}`,
    externalUrl: buildKindredWebUrl(`/adventures/${adventure.id}`),
    accessScope: "public" as const,
  };
  const canOpenMaps = hasMapTarget({
    title: adventure.title,
    location: adventure.location,
  });
  const mapsSearchUrl = canOpenMaps
    ? buildGoogleMapsSearchUrl({
        title: adventure.title,
        location: adventure.location,
      })
    : null;
  const mapsDirectionsUrl = canOpenMaps
    ? buildGoogleMapsDirectionsUrl({
        title: adventure.title,
        location: adventure.location,
      })
    : null;
  const venueUrl = normalizeExternalUrl(adventure.venue_url);
  const whatsappChatUrl = normalizeWhatsAppChatUrl(adventure.whatsapp_chat_url);

  const handleExternalShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: sharePayload.title,
          text: buildKindredExternalShareText(sharePayload),
          url: sharePayload.externalUrl,
        });
      } catch {
        return;
      }
      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(sharePayload.externalUrl);
      toast({ title: "Activity link copied" });
      return;
    }

    window.prompt("Copy this link", sharePayload.externalUrl);
  };

  const activityChatCard = canAccessChat ? (
    <AppCard>
      <AppCardBody
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap="$md"
        $xs={{ flexDirection: "column", alignItems: "stretch" }}
      >
        <AppSectionHeader
          title="Activity chat"
          subtitle="Open the conversation with the organiser and people going."
        />
        <AppButton
          type="button"
          tone="outline"
          onPress={() => navigate(`/adventures/${adventure.id}/chat`)}
        >
          <MessageCircle size={16} /> Open chat
        </AppButton>
      </AppCardBody>
    </AppCard>
  ) : null;

  return (
    <MemberPageScaffold
      title={adventure.title}
      subtitle={`${typeConfig.emoji} ${typeConfig.label} · ${formatDateRange(adventure.date_start, adventure.date_end)} · ${adventure.location}`}
      fallbackPath="/activities"
      titleLines={2}
      action={
        <div className="flex items-center gap-2">
          <KindredShareSheet payload={sharePayload} triggerContent={<Share2 size={16} />} iconOnly />
          {canEditAdventure ? (
            <AppButton type="button" tone="outline" size="sm" onPress={() => navigate(`/adventures/edit/${adventure.id}`)}>
              <Pencil size={14} />
              Edit
            </AppButton>
          ) : null}
        </div>
      }
    >
      <AdventureHero
        imageUrl={adventure.image_url}
        storageBucket="adventure-photos"
        badgeLabel={`${typeConfig.emoji} ${typeConfig.label}`}
        className="flex h-48 items-end p-4"
      />

      <AppCard>
        <AppCardBody gap="$md">
          {organiser ? (
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-secondary text-secondary-foreground">
                  {(organiser.display_name ?? "K")[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
                  Organised by {organiser.display_name ?? "Kindred member"}
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                  Member since {new Date(organiser.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                </AppText>
              </div>
            </div>
          ) : null}

          <AppSection>
            <DetailMetaRow icon={Calendar}>{formatDateRange(adventure.date_start, adventure.date_end)}</DetailMetaRow>
            {mapsSearchUrl ? (
              <a href={mapsSearchUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                <DetailMetaRow icon={MapPin}>{adventure.location}</DetailMetaRow>
              </a>
            ) : (
              <DetailMetaRow icon={MapPin}>{adventure.location}</DetailMetaRow>
            )}
            <DetailMetaRow icon={Users}>{formatAgeRange(adventure.min_child_age, adventure.max_child_age)}</DetailMetaRow>
            <DetailMetaRow icon={UserCheck}>Up to {adventure.max_group_size} families</DetailMetaRow>
          </AppSection>

          <AppText lineHeight="$md">{cleanedDescription}</AppText>

          <AppButtonRow>
            <AddToCalendarButton
              title={adventure.title}
              description={cleanedDescription}
              location={adventure.location}
              startDate={new Date(adventure.date_start)}
              endDate={
                adventure.date_end
                  ? new Date(adventure.date_end)
                  : new Date(new Date(adventure.date_start).getTime() + 24 * 60 * 60 * 1000)
              }
            />
            {mapsSearchUrl ? (
              <a href={mapsSearchUrl} target="_blank" rel="noopener noreferrer">
                <AppButton type="button" tone="outline">
                  Open in Maps
                </AppButton>
              </a>
            ) : null}
            {mapsDirectionsUrl ? (
              <a href={mapsDirectionsUrl} target="_blank" rel="noopener noreferrer">
                <AppButton type="button" tone="outline">
                  Get directions
                </AppButton>
              </a>
            ) : null}
            {venueUrl ? (
              <a href={venueUrl} target="_blank" rel="noopener noreferrer">
                <AppButton type="button" tone="outline">
                  <ExternalLink size={16} /> Venue website
                </AppButton>
              </a>
            ) : null}
            {whatsappChatUrl ? (
              <a href={whatsappChatUrl} target="_blank" rel="noopener noreferrer">
                <AppButton type="button" tone="outline">
                  <MessageCircle size={16} /> Open WhatsApp chat
                </AppButton>
              </a>
            ) : null}
            <AppButton type="button" tone="outline" onPress={() => void handleExternalShare()}>
              Share link
            </AppButton>
          </AppButtonRow>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <AppSectionHeader title="Planning checklist" subtitle={checklistSummary ?? checklistGuidance} />

          {isOwner ? (
            <AppFieldGroup label="Add a task">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <AppInput
                  value={taskDraft}
                  onChangeText={setTaskDraft}
                  placeholder="Add a task, for example confirm the booking"
                  aria-label="Add an adventure checklist task"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void addTask();
                    }
                  }}
                  flex={1}
                />
                <AppButton type="button" onPress={() => void addTask()} disabled={addingTask || !taskDraft.trim()}>
                  {addingTask ? "Adding..." : "Add task"}
                </AppButton>
              </div>
            </AppFieldGroup>
          ) : null}

          {tasks.length ? (
            <div className="space-y-2">
              {tasks.map((task) => {
                const disabled = !canToggleTasks || togglingTaskId === task.id;

                return (
                  <div
                    key={task.id}
                    className={`flex items-start gap-3 rounded-card border px-4 py-3 transition-colors ${
                      disabled ? "border-border/60 bg-muted/20" : "border-border/70 bg-card/90 hover:border-primary/30"
                    }`}
                  >
                    <AppCheckbox
                      checked={task.completed}
                      onCheckedChange={() => void toggleTask(task)}
                      disabled={disabled}
                    />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-6 ${task.completed ? "text-muted-foreground line-through" : "text-foreground"}`}>
                        {task.title}
                      </p>
                      {task.completed ? (
                        <p className="text-xs text-muted-foreground">
                          {task.completed_by === user?.id ? "Checked off by you" : "Checked off"}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <AppEmptyState
              title="No checklist tasks yet"
              body={
                isOwner
                  ? "Add the first checklist item for this activity."
                  : "The organiser can add planning tasks here."
              }
            />
          )}

          {tasks.length > 0 || !isOwner ? (
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              {checklistGuidance}
            </AppText>
          ) : null}
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <AppSectionHeader title="Adventure memories" subtitle={memoryGuidance} />

          {adventureComplete && canPostMemories ? (
            <AppSection>
              <AppFieldGroup label="Add a memory">
                <AppTextArea
                  value={memoryCaption}
                  onChangeText={setMemoryCaption}
                  maxLength={400}
                  placeholder="Add a memory from the day, a photo caption, or something you want this group to remember."
                />
              </AppFieldGroup>
              <AppButtonRow justifyContent="space-between">
                <PhotoUploadButton
                  bucket="adventure-photos"
                  onPhotoReady={setMemoryPhotoUrl}
                  onClear={() => setMemoryPhotoUrl(null)}
                  previewUrl={memoryPhotoUrl}
                  buttonLabel={memoryPhotoUrl ? "Replace photo" : "Add a photo"}
                  returnObjectPath
                />
                <AppButton type="button" onPress={() => void addMemory()} disabled={postingMemory || !memoryCaption.trim()}>
                  {postingMemory ? "Saving..." : "Save memory"}
                </AppButton>
              </AppButtonRow>
            </AppSection>
          ) : null}

          {memories.length ? (
            <div className="space-y-3">
              {memories.map((memory) => (
                <AppCard key={memory.id} backgroundColor="$background">
                  <AppCardBody>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-secondary text-secondary-foreground">
                          {(memory.profiles?.display_name ?? "K")[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
                          {memory.profiles?.display_name ?? "Kindred member"}
                        </AppText>
                        <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                          {new Date(memory.created_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </AppText>
                      </div>
                    </div>
                    <AppText lineHeight="$md">{memory.caption}</AppText>
                    {memory.image_url && resolveStoredObjectUrl("adventure-photos", memory.image_url, resolvedMemoryImageUrls) ? (
                      <button
                        type="button"
                        onClick={() => setFullScreenImage(resolveStoredObjectUrl("adventure-photos", memory.image_url, resolvedMemoryImageUrls))}
                        className="block overflow-hidden rounded-card"
                      >
                        <img
                          src={resolveStoredObjectUrl("adventure-photos", memory.image_url, resolvedMemoryImageUrls) ?? ""}
                          alt=""
                          className="h-48 w-full object-cover"
                        />
                      </button>
                    ) : null}
                  </AppCardBody>
                </AppCard>
              ))}
            </div>
          ) : (
            <AppEmptyState
              title="No memories yet"
              body={
                adventureComplete
                  ? "Be the first to save something from this activity."
                  : "Once the activity has finished, memories will stay here permanently."
              }
            />
          )}
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <AppSectionHeader
            title="Who’s going"
            subtitle={`${interestedCount} interested · ${goingCount} going${waitlistCount > 0 ? ` · ${waitlistCount} waiting` : ""}`}
          />

          {goingMembers.length > 0 ? (
            <div className="flex -space-x-2">
              {goingMembers.slice(0, 5).map((member) => (
                <Avatar key={member.id} className="h-8 w-8 border-2 border-card">
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {(member.profiles?.display_name ?? "K")[0]}
                  </AvatarFallback>
                </Avatar>
              ))}
              {goingMembers.length > 5 ? (
                <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-muted text-xs font-medium text-muted-foreground">
                  +{goingMembers.length - 5}
                </div>
              ) : null}
            </div>
          ) : null}

          {adventureIsFull ? (
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              This activity is full right now. New places go through the waitlist first.
            </AppText>
          ) : null}

          <AppButtonRow>
            {myStatus === "joined" ? (
              <>
                <AppButton type="button" disabled flex={1}>
                  You&apos;re going! ✓
                </AppButton>
                <AppButton type="button" tone="outline" onPress={() => void leave()} disabled={acting} flex={1}>
                  Leave activity
                </AppButton>
              </>
            ) : myStatus === "waitlist" ? (
              <>
                {!adventureIsFull ? (
                  <AppButton type="button" onPress={() => void join("joined")} disabled={acting} flex={1}>
                    A place has opened — Join this activity
                  </AppButton>
                ) : (
                  <AppButton type="button" disabled flex={1}>
                    On waitlist ✓
                  </AppButton>
                )}
                <AppButton type="button" tone="outline" onPress={() => void leave()} disabled={acting} flex={1}>
                  Leave waitlist
                </AppButton>
              </>
            ) : myStatus === "interested" ? (
              <>
                {!adventureIsFull ? (
                  <AppButton type="button" onPress={() => void join("joined")} disabled={acting} flex={1}>
                    I&apos;m in — Join this activity
                  </AppButton>
                ) : (
                  <AppButton type="button" onPress={() => void join("waitlist")} disabled={acting} flex={1}>
                    Join waitlist
                  </AppButton>
                )}
                <AppButton type="button" tone="outline" disabled flex={1}>
                  Interested ✓
                </AppButton>
                <AppButton type="button" tone="outline" onPress={() => void leave()} disabled={acting} flex={1}>
                  Remove interest
                </AppButton>
              </>
            ) : (
              <>
                {!adventureIsFull ? (
                  <AppButton type="button" onPress={() => void join("joined")} disabled={acting} flex={1}>
                    I&apos;m in — Join this activity
                  </AppButton>
                ) : (
                  <AppButton type="button" onPress={() => void join("waitlist")} disabled={acting} flex={1}>
                    Join waitlist
                  </AppButton>
                )}
                <AppButton type="button" tone="outline" onPress={() => void join("interested")} disabled={acting} flex={1}>
                  I&apos;m interested
                </AppButton>
              </>
            )}
          </AppButtonRow>
        </AppCardBody>
      </AppCard>

      {activityChatCard}

      {fullScreenImage ? (
        <FullScreenImage
          images={[fullScreenImage]}
          currentIndex={0}
          onClose={() => setFullScreenImage(null)}
          onNavigate={() => {}}
        />
      ) : null}
    </MemberPageScaffold>
  );
};

export default AdventureDetail;
